import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import React from 'react'
import { trackDirectoryRecoveryClick } from 'src/utils/pixels/directoryEvents'

/**
 * The disclaimer band (plan §2.3).
 *
 * This is not decoration and not boilerplate — it is the sentence that keeps a
 * directory ficha from reading as a SpotMe listing. Publishing a real
 * business's details next to SpotMe's brand without it is exactly the
 * code↔marketing drift that is actionable under Mexican consumer law.
 *
 * ⚠️ The wording is part of the batch that goes to legal before public launch
 * (plan → "Legal gate"), together with the privacy-policy clause and the
 * price-attribution copy. Do not soften "no lo operamos, no lo verificamos y
 * no lo representamos" without that review.
 *
 * The link under it (2026-09-26) adds no claim: the sentence already names
 * "los espacios verificados", and ~50 people a month tapped the band itself
 * with nowhere to go. On mobile it is the only ramp to verified supply above
 * the fold — the NEARBY card sits below the reveal form.
 */
const HEADING_ID = 'directory-disclaimer-heading'

interface DirectoryDisclaimerProps {
  locationId: string
  /** The verified supply page for this metro + category (buildVerifiedNearbyHref).
   *  `null` in a zero-supply area: the link would land on a page with no
   *  verified spaces, so it is omitted — the sentence itself never changes. */
  verifiedNearbyHref: string | null
}

const DirectoryDisclaimer = ({
  locationId,
  verifiedNearbyHref
}: DirectoryDisclaimerProps) => {
  const { t } = useTranslation('directorio')

  return (
    // Named landmark + a real heading: an <aside> inside <main> maps to an
    // unnamed `complementary` role, so without these the copy the plan calls
    // legally load-bearing is reachable by neither landmark nor heading
    // navigation.
    <aside
      aria-labelledby={HEADING_ID}
      className="rounded-xl border border-orange-800/25 bg-orange-800/5 p-4 md:p-5"
      data-testid="directory-disclaimer"
    >
      <h2
        id={HEADING_ID}
        className="font-display text-[15px] leading-tight font-medium text-ink-700 md:text-base"
      >
        {t('FICHA.DISCLAIMER_TITLE')}
      </h2>
      <p className="mt-1.5 text-[13px] leading-relaxed text-ink-200 md:text-sm">
        {t('FICHA.DISCLAIMER_BODY')}
      </p>
      {verifiedNearbyHref && (
        <Link
          href={verifiedNearbyHref}
          onClick={() =>
            trackDirectoryRecoveryClick({
              locationId,
              target: 'verified_nearby_disclaimer'
            })
          }
          className="mt-3 inline-flex items-center gap-1.5 py-1 text-[13px] font-medium text-blue-800 underline decoration-blue-800/30 underline-offset-4 transition-colors duration-200 hover:decoration-blue-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-700 md:text-sm"
        >
          {t('FICHA.NEARBY_CTA')}
          <i className="si si-right text-[8px]" aria-hidden="true" />
        </Link>
      )}
    </aside>
  )
}

export default React.memo(DirectoryDisclaimer)
